import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { PlaceholderKey, isKnownPlaceholderKey } from './placeholder-keys';
import type { PlaceholderData } from './types';

export interface ResolverTeam {
  id: number;
  name: string;
  shortName: string;
  logoUrl?: string;
}

export interface ResolverChampionship {
  id: number;
  name: string;
  logoUrl?: string;
  startDate?: string;
}

export interface ResolverTournament {
  id: number;
  name: string;
  logoUrl?: string;
}

export interface ResolverMatch {
  id: number;
  date?: string;
  venue?: string;
  homeScore?: number;
  awayScore?: number;
  roundName?: string;
}

export interface ResolverPlayer {
  id: number;
  name: string;
  number?: number;
  photoUrl?: string;
}

/**
 * A single match slot for multi-match templates (MULTIPLE_SCHEDULE,
 * MULTIPLE_RESULTS). Each slot is the full per-match bundle the resolver
 * needs — the match itself plus its two teams. A placeholder with
 * `matchNumber=N` resolves against `matches[N-1]`.
 */
export interface ResolverMatchSlot {
  match: ResolverMatch;
  homeTeam: ResolverTeam;
  awayTeam: ResolverTeam;
  homeRoster?: ResolverPlayer[];
  awayRoster?: ResolverPlayer[];
}

export interface ResolverContext {
  tournament: ResolverTournament;
  championship: ResolverChampionship;
  match: ResolverMatch;
  homeTeam: ResolverTeam;
  awayTeam: ResolverTeam;
  homeRoster?: ResolverPlayer[];
  awayRoster?: ResolverPlayer[];
  /**
   * Multi-match payload. Populated only for templates that render several
   * matches at once (MULTIPLE_SCHEDULE, MULTIPLE_RESULTS). When set, a
   * placeholder with `matchNumber=N` resolves against `matches[N-1]`;
   * placeholders with no `matchNumber` still fall back to the top-level
   * single-match fields. `matches[0]` typically mirrors the top-level
   * single-match fields so legacy templates keep rendering unchanged.
   */
  matches?: readonly ResolverMatchSlot[];
  /**
   * Active template subtypes (e.g. indicator subtypes for the top-players
   * template). Forwarded by `substitutePlaceholders` from the template row so
   * subtype-aware placeholder keys added later can branch on it. Empty when
   * the active type has no subtype dimension.
   */
  templateSubtypes?: readonly string[];
}

export type ResolvedValue = { type: 'text'; value: string } | { type: 'image'; url: string };

export function resolvePlaceholder(data: PlaceholderData, ctx: ResolverContext): ResolvedValue | null {
  if (!isKnownPlaceholderKey(data.placeholderKey)) return null;
  const key = data.placeholderKey as PlaceholderKey;
  const slot = pickMatchSlot(data.matchNumber, ctx);
  if (slot === null) return null;
  const teamSlot = pickTeam(data.teamNumber, slot);

  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return teamSlot ? text(teamSlot.name) : null;
    case PlaceholderKey.TEAM_SHORT_NAME:
      return teamSlot ? text(teamSlot.shortName) : null;
    case PlaceholderKey.TEAM_LOGO:
      return teamSlot?.logoUrl ? image(teamSlot.logoUrl) : null;
    case PlaceholderKey.SCORE: {
      const template = (data.kind === 'placeholder-text' && data.scoreFormat) || DEFAULT_SCORE_FORMAT;
      const home = String(slot.match.homeScore ?? 0);
      const away = String(slot.match.awayScore ?? 0);
      return text(template.replace(/t1/g, home).replace(/t2/g, away));
    }
    case PlaceholderKey.SCORE_TEAM: {
      if (!data.teamNumber) return null;
      const score = data.teamNumber === 1 ? slot.match.homeScore : slot.match.awayScore;
      return text(String(score ?? 0));
    }
    case PlaceholderKey.CHAMPIONSHIP_NAME:
      return text(ctx.championship.name);
    case PlaceholderKey.TOURNAMENT_NAME:
      return text(ctx.tournament.name);
    case PlaceholderKey.TOURNAMENT_LOGO:
      return ctx.tournament.logoUrl ? image(ctx.tournament.logoUrl) : null;
    case PlaceholderKey.MATCH_DATE: {
      if (!slot.match.date) return null;
      const customFormat = data.kind === 'placeholder-text' ? data.dateFormat?.trim() : undefined;
      return text(safeFormatDate(slot.match.date, customFormat || DEFAULT_DATE_FORMAT));
    }
    case PlaceholderKey.MATCH_TIME:
      return slot.match.date ? text(format(new Date(slot.match.date), 'HH:mm', { locale: ru })) : null;
    case PlaceholderKey.MATCH_VENUE:
      return slot.match.venue ? text(slot.match.venue) : null;
    case PlaceholderKey.ROUND_NAME:
      return slot.match.roundName ? text(slot.match.roundName) : null;
    case PlaceholderKey.PLAYER_NAME: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player ? text(player.name) : null;
    }
    case PlaceholderKey.PLAYER_NUMBER: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player && typeof player.number === 'number' ? text(String(player.number)) : null;
    }
    case PlaceholderKey.PLAYER_PHOTO: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player?.photoUrl ? image(player.photoUrl) : null;
    }
  }

  return null;
}

// Returns the per-match slot to resolve against. When `matchNumber` is set and
// `ctx.matches` is non-empty, picks `matches[matchNumber-1]` — returns `null`
// (drop the placeholder) when the index is out of range, so an editor that
// asks for match #4 of a 3-match render doesn't silently fall through to
// match #1. When `matchNumber` is unset or `ctx.matches` is empty, falls
// back to the top-level single-match fields to preserve SCHEDULE/RESULTS.
function pickMatchSlot(matchNumber: number | undefined, ctx: ResolverContext): ResolverMatchSlot | null {
  if (matchNumber && matchNumber >= 1) {
    const slot = ctx.matches?.[matchNumber - 1];
    return slot ?? null;
  }
  return {
    match: ctx.match,
    homeTeam: ctx.homeTeam,
    awayTeam: ctx.awayTeam,
    homeRoster: ctx.homeRoster,
    awayRoster: ctx.awayRoster,
  };
}

function pickTeam(teamNumber: number | undefined, slot: ResolverMatchSlot): ResolverTeam | undefined {
  if (teamNumber === 1) return slot.homeTeam;
  if (teamNumber === 2) return slot.awayTeam;
  return undefined;
}

function pickPlayer(
  teamNumber: number | undefined,
  playerNumber: number | undefined,
  slot: ResolverMatchSlot,
): ResolverPlayer | undefined {
  if (!playerNumber || playerNumber < 1) return undefined;
  const roster = teamNumber === 1 ? slot.homeRoster : teamNumber === 2 ? slot.awayRoster : undefined;
  return roster?.[playerNumber - 1];
}

function text(value: string): ResolvedValue {
  return { type: 'text', value };
}

function image(url: string): ResolvedValue {
  return { type: 'image', url };
}

export const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
export const DEFAULT_SCORE_FORMAT = 't1:t2';

function safeFormatDate(iso: string, pattern: string): string {
  try {
    return format(new Date(iso), pattern, { locale: ru });
  } catch {
    return format(new Date(iso), DEFAULT_DATE_FORMAT, { locale: ru });
  }
}
